import React, { useEffect, useMemo, useState } from 'react';
import { FaGraduationCap, FaChartBar, FaMedal, FaPlusCircle, FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import UserAvatar from '../components/UserAvatar';

function useDebounce(v, d=350){
  const [s, setS] = useState(v);
  useEffect(()=>{ const t=setTimeout(()=>setS(v), d); return ()=>clearTimeout(t); },[v,d]);
  return s;
}

export default function Education(){
  /* ----------------------------- State & Fetch ----------------------------- */
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const debouncedSearch               = useDebounce(search, 350);
  const [page, setPage]               = useState(1);
  const pageSize = 10;

  useEffect(() => {
    async function fetchLeaderboard(){
      setLoading(true); setError('');
      try{
        const res = await fetch('https://dev.sliitleo.org/Backend/get_leaderboard.php');
        const data = await res.json();
        if (!data?.success || !Array.isArray(data.leaderboard)) throw new Error('Invalid data');
        setLeaderboard(data.leaderboard);
      } catch {
        setError('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    }
    fetchLeaderboard();
  }, []);

  /* ----------------------------- Derived lists ----------------------------- */
  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return leaderboard;
    const s = debouncedSearch.toLowerCase();
    return leaderboard.filter(u =>
      (u.name || '').toLowerCase().includes(s) ||
      String(u.score || '').toLowerCase().includes(s)
    );
  }, [leaderboard, debouncedSearch]);

  useEffect(() => { if ((page-1)*pageSize >= filtered.length) setPage(1); }, [filtered, page]);

  const paged = useMemo(() => filtered.slice((page-1)*pageSize, page*pageSize), [filtered, page]);

  /* ----------------------------- User meta ----------------------------- */
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User', role: 'Member', image: '/default-user.png',
  };

  /* -------------------------------- Render -------------------------------- */
  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1>
              <FaGraduationCap style={{ marginRight: 8, color: 'var(--accent)' }} />
              Education Admin <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>/ Leaderboard</span>
            </h1>
            <div className="page-desc">Search and browse quiz leaderboard rankings.</div>
          </div>

          <div className="filter-row" style={{ flexWrap: 'wrap' }}>
            <Link to="/education-statistics" className="btn btn-ghost btn-sm">
              <FaChartBar /> Statistics
            </Link>
            <Link to="/education-leaderboard" className="btn btn-ghost btn-sm">
              <FaMedal /> Leaderboard
            </Link>
            <Link to="/education-add-quiz-question" className="btn btn-primary btn-sm">
              <FaPlusCircle /> Add Quiz
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="panel" style={{ padding: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field-label" htmlFor="leaderboard-search">Search leaderboard</label>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', fontSize: 13 }} aria-hidden="true" />
              <input
                id="leaderboard-search"
                type="search"
                value={search}
                onChange={e=>setSearch(e.target.value)}
                placeholder="Type a name or score…"
                style={{ paddingLeft: 36 }}
              />
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="panel" style={{ padding: 'var(--sp-5)' }}>
          <div className="section-head" style={{ padding: 0, marginBottom: 'var(--sp-4)' }}>
            <h2>Leaderboard</h2>
          </div>

          {/* Loading / error states */}
          {loading ? (
            <>
              {/* Podium skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ marginBottom: 'var(--sp-6)' }}>
                {Array.from({length:3}).map((_,i)=>(
                  <div key={i} className="panel" style={{ padding: 'var(--sp-4)' }}>
                    <div className="skeleton" style={{ width: 40, height: 40, borderRadius: '50%', marginBottom: 12 }} />
                    <div className="skeleton" style={{ width: '70%', height: 14, marginBottom: 8 }} />
                    <div className="skeleton" style={{ width: '40%', height: 18 }} />
                  </div>
                ))}
              </div>
              {/* Rows skeleton */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({length:6}).map((_,i)=>(
                  <div key={i} className="panel" style={{ padding: 'var(--sp-3) var(--sp-4)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="skeleton" style={{ width: 24, height: 24 }} />
                    <div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                    <div style={{ flex: 1 }}>
                      <div className="skeleton" style={{ width: 160, height: 14, marginBottom: 6 }} />
                      <div className="skeleton" style={{ width: 90, height: 11 }} />
                    </div>
                    <div className="skeleton" style={{ width: 48, height: 18 }} />
                  </div>
                ))}
              </div>
            </>
          ) : error ? (
            <div className="error-banner">{error}</div>
          ) : (
            <>
              {/* Podium Top-3 */}
              {filtered.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ marginBottom: 'var(--sp-6)' }}>
                  {['🥇','🥈','🥉'].map((medal, i) => {
                    const u = filtered[i];
                    return u ? (
                      <div
                        key={i}
                        className="panel"
                        style={{ padding: 'var(--sp-4)', display: 'flex', alignItems: 'center', gap: 14, borderColor: 'var(--accent)' }}
                      >
                        <div style={{ fontSize: 28, flexShrink: 0 }} aria-hidden="true">{medal}</div>
                        <UserAvatar src={u.avatar} alt={u.name} size={48} fallback="/avatars/default.png" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="proj-name truncate">{u.name}</div>
                          <div className="proj-cat">Score</div>
                        </div>
                        <div className="num" style={{ color: 'var(--success)', fontSize: 18, fontWeight: 700 }}>{u.score}</div>
                      </div>
                    ) : (
                      <div key={i} className="panel" style={{ padding: 'var(--sp-4)', textAlign: 'center', color: 'var(--text-3)', opacity: .6 }}>—</div>
                    );
                  })}
                </div>
              )}

              {/* Rows */}
              {filtered.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><FaSearch /></div>
                  <h3>No results</h3>
                  <p>No leaderboard entries match your search.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Member</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((u, idx) => {
                        const absoluteRank = (page-1)*pageSize + idx;
                        const isTop = absoluteRank < 3;
                        return (
                          <tr key={u.id || u.name}>
                            <td className="num" style={{ fontWeight: 700, color: isTop ? 'var(--accent)' : 'var(--text-2)' }}>
                              {absoluteRank === 0 ? '🥇' : absoluteRank === 1 ? '🥈' : absoluteRank === 2 ? '🥉' : `#${absoluteRank + 1}`}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <UserAvatar src={u.avatar} alt={u.name} size={32} fallback="/avatars/default.png" />
                                <div style={{ minWidth: 0 }}>
                                  <div className="proj-name truncate">{u.name}</div>
                                  <div className="proj-cat">Participant</div>
                                </div>
                              </div>
                            </td>
                            <td className="num" style={{ fontWeight: 700, color: 'var(--success)' }}>{u.score}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {filtered.length > pageSize && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--sp-5)', flexWrap: 'wrap', gap: 10 }}>
                  <div className="num" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                    Showing {(page-1)*pageSize + 1}
                    {"–"}
                    {Math.min(page*pageSize, filtered.length)} of {filtered.length}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={()=>setPage(p=>Math.max(1,p-1))}
                      disabled={page===1}
                    >
                      Prev
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={()=>setPage(p=> (p*pageSize<filtered.length ? p+1 : p))}
                      disabled={page*pageSize>=filtered.length}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}
